import * as React from "react";
import { useCallback, useMemo } from "react";
import styled, { css } from "styled-components";
import { Form, setNestedObjectValues } from "formik";
import { flushSync } from "react-dom";
import useReportForm from "report/report-creation/useReportForm";
import WidgetDetailsContext from "report/report-creation/WidgetDetailsContext";
import { Row, Col, Button } from "components/bootstrap";
import Icon from "components/common/Icon";
import FormatSelect from "report/report-creation/FormatSelect";
import ReportCreationNavigation from "report/report-creation/ReportCreationNavigation";
import ReportCreationRouter from "report/report-creation/ReportCreationRouter";
import ReportPreview from "report/report-creation/ReportPreview";
import ReportActions from "report/report-creation/ReportActions";
import { DEFAULT_FORMAT } from "report/types";
import useHistory from "routing/useHistory";
import ReportRoutes from "report/ReportRoutes";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import UserNotification from "util/UserNotification";
import { generateReport, showDownloadDialog } from "report/report-creation/overview/download";
import useWidgetDetails from "report/hooks/useWidgetDetails";
import { Spinner, ModalButtonToolbar } from "components/common";
const SIDEBAR_WIDTH = 4;
const StyledForm = styled(Form)(
  ({ theme }) => css`
    height: 100%;

    @media (max-width: ${theme.breakpoints.max.md}) {
      height: auto;
    }
  `
);
const ContentRow = styled(Row)(
  ({ theme }) => css`
    height: 100%;

    @media (max-width: ${theme.breakpoints.max.md}) {
      height: auto;
    }
  `
);
const ContentCol = styled(Col)(
  ({ theme }) => css`
    height: 100%;
    display: flex;
    flex-direction: column;

    @media (max-width: ${theme.breakpoints.max.md}) {
      height: auto;
    }
  `
);
const HeaderCol = styled(Col)`
  display: flex;
  padding: 0 15px;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;
const GrowingRow = styled(Row)`
  flex: 1;
`;
const SidebarCol = styled(Col)`
  height: 100%;
  display: flex;
  flex-direction: column;
`;
const FormContainer = styled.div(
  ({ theme }) => css`
    padding: 0 15px;
    height: 100%;
    flex-grow: 1;
    flex-basis: 0;
    overflow: auto;

    @media (max-width: ${theme.breakpoints.max.md}) {
      height: auto;
      overflow: initial;
    }
  `
);
const PreviewCol = styled(Col)(
  ({ theme }) => css`
    height: 100%;
    overflow: hidden;
    padding: 0 15px;
    display: flex;
    flex-direction: column;

    @media (max-width: ${theme.breakpoints.max.md}) {
      min-height: 500px;
    }
  `
);
const SubmitCol = styled(Col)`
  padding: 15px 15px 0;
`;
const firstErroneousPage = (errors) => {
  if (errors.title) {
    return "overview";
  }
  if ((errors == null ? void 0 : errors.delivery) && Object.keys(errors.delivery)) {
    return "delivery";
  }
  if ((errors == null ? void 0 : errors.layout) && Object.keys(errors.layout)) {
    return "style";
  }
  return "content";
};
const redirectToErroneousPage = (errors, history) => {
  const page = firstErroneousPage(errors);
  history.push(page === "overview" ? "" : page);
};
const useSubmitHandlers = (action) => {
  const history = useHistory();
  const sendTelemetry = useSendTelemetry("report-form");
  const { values, validateForm, submitForm, setTouched, resetForm } = useReportForm();
  const trySubmit = useCallback(
    async (submitAction) => {
      var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l;
      const validationErrors = await validateForm();
      if (Object.keys(validationErrors).length > 0) {
        setTouched(setNestedObjectValues(validationErrors, true));
        UserNotification.error(
          "Some changes were not be saved, please check for missing required fields before closing."
        );
        redirectToErroneousPage(validationErrors, history);
        return Promise.resolve();
      }
      sendTelemetry(TELEMETRY_EVENT_TYPE.REPORT[`${action === "create" ? "CREATED" : "UPDATED"}`], {
        app_pathname: "report",
        event_details: {
          format: (_b = (_a = values.layout) == null ? void 0 : _a.format) != null ? _b : DEFAULT_FORMAT,
          widget_count: (_d = (_c = values.widgets) == null ? void 0 : _c.length) != null ? _d : 0,
          submit_type: submitAction,
          overview: {
            timezone: values.timezone,
            schedule: values.delivery.scheduling
          },
          delivery: {
            user_receivers_count: (_f = (_e = values.delivery.user_receivers) == null ? void 0 : _e.length) != null ? _f : 0,
            email_receivers_count: (_h = (_g = values.delivery.email_receivers) == null ? void 0 : _g.length) != null ? _h : 0
          },
          style: {
            layout: {
              page_size: (_i = values.layout) == null ? void 0 : _i.pageSize,
              page_orientation: (_j = values.layout) == null ? void 0 : _j.orientation,
              showFooter: (_k = values.layout) == null ? void 0 : _k.footer,
              print_toc: (_l = values.layout) == null ? void 0 : _l.printToc
            },
            hideWidgetDescription: values.hideWidgetDescription,
            hideWidgetQuery: values.hideWidgetQuery
          }
        }
      });
      return submitForm();
    },
    [action, history, sendTelemetry, setTouched, submitForm, validateForm, values]
  );
  const resetFormDirty = useCallback(() => {
    flushSync(() => resetForm({ values }));
  }, [resetForm, values]);
  const goToEditPageAfterCreation = useCallback(
    (beReport) => {
      if (action === "create") {
        resetFormDirty();
        history.push(ReportRoutes.contents(beReport.id));
      }
    },
    [action, history, resetFormDirty]
  );
  const onSubmit = useCallback(async () => {
    const report = await trySubmit("onSubmit");
    if (report) {
      goToEditPageAfterCreation(report);
    }
  }, [goToEditPageAfterCreation, trySubmit]);
  const onSubmitAndGenerate = useCallback(async () => {
    const report = await trySubmit("submitAndGenerate");
    if (report && report.id) {
      const generateResponse = await generateReport(report.id, report.title);
      if (generateResponse.status === 202) {
        UserNotification.success("Report is being generated");
        resetFormDirty();
        history.push(ReportRoutes.archive(report.id));
        return;
      }
      if (generateResponse.ok) {
        await showDownloadDialog(report.title, generateResponse);
        goToEditPageAfterCreation(report);
      } else {
        const blob = await generateResponse.blob();
        const errorMessage = await blob.text();
        UserNotification.error(`Error during report generation: ${errorMessage}`);
      }
    }
  }, [goToEditPageAfterCreation, history, resetFormDirty, trySubmit]);
  const onSubmitAndGoToList = useCallback(async () => {
    const report = await trySubmit("submitAndGoToList");
    if (report && report.id) {
      resetFormDirty();
      history.push(ReportRoutes.OVERVIEW);
    }
  }, [history, resetFormDirty, trySubmit]);
  return { onSubmit, onSubmitAndGenerate, onSubmitAndGoToList };
};
const ReportFormContent = ({ action, pathPrefix, onCancel, title, editAllowed, scopeLocked = false }) => {
  var _a;
  const { values, errors } = useReportForm();
  const { onSubmit, onSubmitAndGenerate, onSubmitAndGoToList } = useSubmitHandlers(action);
  const hasMissingParameters = Object.keys((_a = errors == null ? void 0 : errors.parameterValues) != null ? _a : {}).length > 0;
  const { data: widgetDetails, isLoading } = useWidgetDetails(values == null ? void 0 : values.widgets);
  const widgetDetailsContextValue = useMemo(
    () => ({
      ...widgetDetails,
      isLoading
    }),
    [isLoading, widgetDetails]
  );
  const isCreating = action === "create";
  return !widgetDetails ? /* @__PURE__ */ React.createElement(Spinner, null) : /* @__PURE__ */ React.createElement(WidgetDetailsContext.Provider, { value: widgetDetailsContextValue }, /* @__PURE__ */ React.createElement(StyledForm, null, /* @__PURE__ */ React.createElement(ContentRow, { className: "content" }, /* @__PURE__ */ React.createElement(ContentCol, { sm: 12 }, /* @__PURE__ */ React.createElement(ContentRow, null, /* @__PURE__ */ React.createElement(ContentCol, { md: SIDEBAR_WIDTH }, /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(HeaderCol, null, /* @__PURE__ */ React.createElement(Button, { bsSize: "small", onClick: onCancel }, /* @__PURE__ */ React.createElement(Icon, { name: "arrow_back" }), " Cancel"), /* @__PURE__ */ React.createElement("h1", null, title))), /* @__PURE__ */ React.createElement(GrowingRow, null, /* @__PURE__ */ React.createElement(SidebarCol, null, /* @__PURE__ */ React.createElement(ReportCreationNavigation, { prefix: pathPrefix, isCreating }), /* @__PURE__ */ React.createElement(FormContainer, null, /* @__PURE__ */ React.createElement(
    ReportCreationRouter,
    {
      editAllowed,
      isCreating,
      scopeLocked
    }
  ))))), /* @__PURE__ */ React.createElement(ContentCol, null, /* @__PURE__ */ React.createElement(PreviewCol, null, /* @__PURE__ */ React.createElement(FormatSelect, { disabled: !editAllowed }), /* @__PURE__ */ React.createElement(ReportPreview, { report: values, missingParameters: hasMissingParameters })))), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(SubmitCol, null, editAllowed && /* @__PURE__ */ React.createElement(
    ReportActions,
    {
      submitButtonText: `${action === "create" ? "Create" : "Update"}`,
      onSubmit,
      onSubmitAndGenerate,
      onSubmitAndGoToList
    }
  ), !editAllowed && /* @__PURE__ */ React.createElement(ModalButtonToolbar, null, /* @__PURE__ */ React.createElement(Button, { onClick: onCancel }, "Close"))))))));
};
export default ReportFormContent;
